// src/pages/Posizioni.jsx - Fixed with direct Supabase calls
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { supabase } from '@/lib/supabase';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Edit, Plus, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

const ManagementColumn = ({ title, items, onSelect, selectedId, onAdd, onEdit, onDelete, isLoading, addDisabled = false }) => (
  <Card className="glass-effect border-0 shadow-lg flex flex-col">
    <CardHeader className="p-4 border-b border-slate-100 flex-shrink-0">
      <div className="flex justify-between items-center">
        <CardTitle className="text-lg font-bold text-slate-700">{title}</CardTitle>
        <Button 
          size="sm" 
          onClick={onAdd}
          disabled={addDisabled}
          className={addDisabled ? "opacity-50 cursor-not-allowed" : ""}
        >
          <Plus className="w-4 h-4" />
        </Button>
      </div>
    </CardHeader>
    <CardContent className="p-2 overflow-y-auto flex-grow">
      {isLoading ? (
        <div className="space-y-2">
          {Array(5).fill(0).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
        </div>
      ) : (
        <div className="space-y-1">
          {items.map(item => (
            <div
              key={item.id}
              onClick={() => onSelect(item)}
              className={`flex justify-between items-center p-2 rounded-md cursor-pointer transition-colors ${selectedId === item.id ? 'bg-blue-100' : 'hover:bg-slate-50'}`}
            >
              <span className="font-medium text-slate-800">{item.nome}</span>
              <div className="flex items-center gap-1">
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="h-7 w-7" 
                  onClick={(e) => { e.stopPropagation(); onEdit(item); }}
                >
                  <Edit className="w-4 h-4 text-slate-500" />
                </Button>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="h-7 w-7" 
                  onClick={(e) => { e.stopPropagation(); onDelete(item); }}
                >
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </CardContent>
  </Card>
);

export default function Posizioni() {
  const [selectedTettoia, setSelectedTettoia] = useState(null);
  const [selectedSezione, setSelectedSezione] = useState(null);
  const [dialogState, setDialogState] = useState({ isOpen: false, type: '', mode: 'add', currentItem: null });
  const [formData, setFormData] = useState({ nome: '' });

  const queryClient = useQueryClient();

  // Load data with direct Supabase calls
  const { data: tettoie = [], isLoading: tettoieLoading } = useQuery({
    queryKey: ['tettoie'],
    queryFn: async () => {
      console.log('Loading tettoie...');
      const { data, error } = await supabase
        .from('tettoie')
        .select('*')
        .order('nome');
      if (error) {
        console.error('Tettoie loading error:', error);
        throw error;
      }
      console.log('Tettoie loaded:', data.length);
      return data || [];
    }
  });

  const { data: sezioni = [], isLoading: sezioniLoading } = useQuery({
    queryKey: ['sezioni'],
    queryFn: async () => {
      console.log('Loading sezioni...');
      const { data, error } = await supabase
        .from('sezioni')
        .select('*')
        .order('nome');
      if (error) {
        console.error('Sezioni loading error:', error);
        throw error;
      }
      console.log('Sezioni loaded:', data.length);
      return data || [];
    }
  });

  const { data: campate = [], isLoading: campateLoading } = useQuery({
    queryKey: ['campate'],
    queryFn: async () => {
      console.log('Loading campate...');
      const { data, error } = await supabase
        .from('campate')
        .select('*')
        .order('nome');
      if (error) {
        console.error('Campate loading error:', error);
        throw error;
      }
      console.log('Campate loaded:', data.length);
      return data || [];
    }
  });

  // Tettoia mutations
  const createTettoiaMutation = useMutation({
    mutationFn: async (tettoiaData) => {
      console.log('Creating tettoia:', tettoiaData);
      const { data, error } = await supabase
        .from('tettoie')
        .insert(tettoiaData)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tettoie'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-data'] });
    }
  });

  const updateTettoiaMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const { data: result, error } = await supabase
        .from('tettoie')
        .update(data)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tettoie'] });
    }
  });

  const deleteTettoiaMutation = useMutation({
    mutationFn: async (id) => {
      const { error } = await supabase
        .from('tettoie')
        .delete()
        .eq('id', id);
      if (error) throw error;
      return true;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tettoie'] });
      queryClient.invalidateQueries({ queryKey: ['sezioni'] });
      queryClient.invalidateQueries({ queryKey: ['campate'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-data'] });
    }
  });

  // Sezione mutations
  const createSezioneMutation = useMutation({
    mutationFn: async (sezioneData) => {
      console.log('Creating sezione:', sezioneData);
      const { data, error } = await supabase
        .from('sezioni')
        .insert(sezioneData)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sezioni'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-data'] });
    }
  });

  const updateSezioneMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const { data: result, error } = await supabase
        .from('sezioni')
        .update(data)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sezioni'] });
    }
  });

  const deleteSezioneMutation = useMutation({
    mutationFn: async (id) => {
      const { error } = await supabase
        .from('sezioni')
        .delete()
        .eq('id', id);
      if (error) throw error;
      return true;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sezioni'] });
      queryClient.invalidateQueries({ queryKey: ['campate'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-data'] });
    }
  });

  // Campata mutations
  const createCampataMutation = useMutation({
    mutationFn: async (campataData) => {
      console.log('Creating campata:', campataData);
      const { data, error } = await supabase
        .from('campate')
        .insert(campataData)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campate'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-data'] });
    }
  });

  const updateCampataMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const { data: result, error } = await supabase
        .from('campate')
        .update(data)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campate'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-data'] });
    }
  });

  const deleteCampataMutation = useMutation({
    mutationFn: async (id) => {
      const { error } = await supabase
        .from('campate')
        .delete()
        .eq('id', id);
      if (error) throw error;
      return true;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campate'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-data'] });
    }
  });

  // Reset selections when data changes
  useEffect(() => {
    if (selectedTettoia && !tettoie.find(t => t.id === selectedTettoia.id)) {
      setSelectedTettoia(null);
    }
  }, [tettoie, selectedTettoia]);

  useEffect(() => {
    if (selectedSezione && !sezioni.find(s => s.id === selectedSezione.id)) {
      setSelectedSezione(null);
    }
  }, [sezioni, selectedSezione]);

  const handleSelectTettoia = (tettoia) => {
    if (selectedTettoia?.id === tettoia.id) {
      setSelectedTettoia(null);
    } else {
      setSelectedTettoia(tettoia);
      setSelectedSezione(null);
    }
  };

  const handleSelectSezione = (sezione) => {
    if (selectedSezione?.id === sezione.id) {
      setSelectedSezione(null);
    } else {
      setSelectedSezione(sezione);
    }
  };

  const openDialog = (type, mode, currentItem = null) => {
    setDialogState({ isOpen: true, type, mode, currentItem });
    setFormData({ nome: currentItem?.nome || '' });
  };
  
  const closeDialog = () => {
    setDialogState({ isOpen: false, type: '', mode: 'add', currentItem: null });
    setFormData({ nome: '' });
  };
  
  const handleDelete = async (type, item) => {
    if (!window.confirm(`Sei sicuro di voler eliminare "${item.nome}"?`)) return;
    
    try {
      switch (type) {
        case 'tettoia':
          await deleteTettoiaMutation.mutateAsync(item.id);
          setSelectedTettoia(null);
          break;
        case 'sezione':
          await deleteSezioneMutation.mutateAsync(item.id);
          setSelectedSezione(null);
          break;
        case 'campata':
          await deleteCampataMutation.mutateAsync(item.id);
          break;
      }
    } catch (error) {
      console.error("Errore nell'eliminazione:", error);
      alert("Errore: impossibile eliminare l'elemento. Assicurarsi che non sia utilizzato da altri elementi (es. una tettoia con sezioni non può essere eliminata).");
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    const { type, mode, currentItem } = dialogState;

    try {
      if (mode === 'add') {
        switch (type) {
          case 'tettoia': 
            await createTettoiaMutation.mutateAsync(formData); 
            break;
          case 'sezione': 
            await createSezioneMutation.mutateAsync({ ...formData, tettoia_id: selectedTettoia.id }); 
            break;
          case 'campata': 
            await createCampataMutation.mutateAsync({ ...formData, sezione_id: selectedSezione.id }); 
            break;
        }
      } else { // edit mode
        switch (type) {
          case 'tettoia': 
            await updateTettoiaMutation.mutateAsync({ id: currentItem.id, data: formData }); 
            break;
          case 'sezione': 
            await updateSezioneMutation.mutateAsync({ id: currentItem.id, data: formData }); 
            break;
          case 'campata': 
            await updateCampataMutation.mutateAsync({ id: currentItem.id, data: formData }); 
            break;
        }
      }

      closeDialog();
    } catch (error) {
      console.error("Errore nel salvataggio:", error);
      alert(`Si è verificato un errore durante il salvataggio: ${error.message}`);
    }
  };

  const filteredSezioni = useMemo(() => {
    if (!selectedTettoia) return [];
    return sezioni.filter(s => s.tettoia_id === selectedTettoia.id);
  }, [sezioni, selectedTettoia]);

  const filteredCampate = useMemo(() => {
    if (!selectedSezione) return [];
    return campate.filter(c => c.sezione_id === selectedSezione.id);
  }, [campate, selectedSezione]);
  
  const getDialogTitle = () => {
    const modeText = dialogState.mode === 'add' ? 'Nuova' : 'Modifica';
    switch(dialogState.type) {
      case 'tettoia': return `${modeText} Tettoia`;
      case 'sezione': return `${modeText} Sezione`;
      case 'campata': return `${modeText} Campata`;
      default: return '';
    }
  };

  const isAnyMutationPending = 
    createTettoiaMutation.isPending || updateTettoiaMutation.isPending || deleteTettoiaMutation.isPending ||
    createSezioneMutation.isPending || updateSezioneMutation.isPending || deleteSezioneMutation.isPending ||
    createCampataMutation.isPending || updateCampataMutation.isPending || deleteCampataMutation.isPending;

  return (
    <div className="p-4 md:p-8 min-h-screen">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-800 mb-2">Gestione Struttura Parcheggio</h1>
          <p className="text-slate-600">Definisci la struttura gerarchica del parcheggio: Tettoie, Sezioni e Campate.</p>
          {/* Debug info */}
          {/* 
          <div className="text-xs text-gray-500 mt-2">
            Debug: Tettoie={tettoie.length}, Sezioni={sezioni.length}, Campate={campate.length}
          </div>
          */}
        </div>

        <div className="grid md:grid-cols-3 gap-6 h-[60vh]">
          <ManagementColumn
            title="Tettoie"
            items={tettoie}
            onSelect={handleSelectTettoia}
            selectedId={selectedTettoia?.id}
            onAdd={() => openDialog('tettoia', 'add')}
            onEdit={(item) => openDialog('tettoia', 'edit', item)}
            onDelete={(item) => handleDelete('tettoia', item)}
            isLoading={tettoieLoading}
          />
          <ManagementColumn
            title={selectedTettoia ? `Sezioni di ${selectedTettoia.nome}` : "Sezioni"}
            items={filteredSezioni}
            onSelect={handleSelectSezione}
            selectedId={selectedSezione?.id}
            onAdd={() => selectedTettoia && openDialog('sezione', 'add')}
            onEdit={(item) => openDialog('sezione', 'edit', item)}
            onDelete={(item) => handleDelete('sezione', item)}
            isLoading={sezioniLoading}
            addDisabled={!selectedTettoia}
          />
          <ManagementColumn
            title={selectedSezione ? `Campate di ${selectedSezione.nome}` : "Campate"}
            items={filteredCampate}
            onSelect={() => {}} // No action on campata selection
            onAdd={() => selectedSezione && openDialog('campata', 'add')}
            onEdit={(item) => openDialog('campata', 'edit', item)}
            onDelete={(item) => handleDelete('campata', item)}
            isLoading={campateLoading}
            addDisabled={!selectedSezione}
          />
        </div>
      </div>
      
      <Dialog open={dialogState.isOpen} onOpenChange={closeDialog}>
        <DialogContent className="glass-effect">
          <DialogHeader>
            <DialogTitle>{getDialogTitle()}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="p-4 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="nome">Nome</Label>
                <Input 
                  id="nome" 
                  value={formData.nome}
                  onChange={(e) => setFormData({...formData, nome: e.target.value})}
                  required
                  disabled={isAnyMutationPending}
                />
              </div>
            </div>
            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                onClick={closeDialog}
                disabled={isAnyMutationPending}
              >
                Annulla
              </Button>
              <Button 
                type="submit"
                disabled={isAnyMutationPending}
              >
                {isAnyMutationPending ? 'Salvando...' : 'Salva'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}